// Pure logic for the built-in signage widgets (clock / weather / ticker / qr /
// countdown / nowplaying / news / menu). DOM- and RN-free and unit-tested; the
// web components under artifacts/web/src/player/components/widgets/ and the
// native views under ../signage-native are thin renderers over these helpers.
//
// Strings come from the shared `player.widgets.*` catalog
// (`@workspace/strings/common`) so every surface renders identical copy.

import { t } from "@workspace/strings/common";

type Config = Record<string, unknown> | null | undefined;

function str(config: Config, key: string): string | null {
  const v = config?.[key];
  return typeof v === "string" && v.length > 0 ? v : null;
}

function num(config: Config, key: string): number | null {
  const v = config?.[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

// ── clock ────────────────────────────────────────────────────────────────────

export interface ClockConfig {
  /** IANA timezone; null = the device's local time. */
  timeZone: string | null;
  hour12: boolean;
}

export function clockConfig(config: Config): ClockConfig {
  return {
    timeZone: str(config, "timeZone"),
    hour12: config?.["hour12"] === true,
  };
}

export function formatClock(
  now: Date,
  cfg: ClockConfig,
): { time: string; date: string } {
  const tz = cfg.timeZone ?? undefined;
  let time: string;
  let date: string;
  try {
    time = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: cfg.hour12,
      timeZone: tz,
    }).format(now);
    date = new Intl.DateTimeFormat("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      timeZone: tz,
    }).format(now);
  } catch {
    // Bad timeZone in config: fall back to device-local.
    time = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: cfg.hour12,
    }).format(now);
    date = new Intl.DateTimeFormat("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(now);
  }
  return { time, date };
}

// ── weather ──────────────────────────────────────────────────────────────────

export const WEATHER_CACHE_TTL_MS = 15 * 60 * 1000;

export interface WeatherConfig {
  lat: number;
  lon: number;
  label: string | null;
}

/** null when the config lacks usable coordinates. */
export function weatherConfig(config: Config): WeatherConfig | null {
  const lat = num(config, "lat");
  const lon = num(config, "lon");
  if (lat == null || lon == null) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { lat, lon, label: str(config, "label") };
}

export function openMeteoUrl(cfg: WeatherConfig): string {
  const p = new URLSearchParams({
    latitude: String(cfg.lat),
    longitude: String(cfg.lon),
    current: "temperature_2m,weather_code",
    timezone: "auto",
  });
  return `https://api.open-meteo.com/v1/forecast?${p.toString()}`;
}

/** WMO weather interpretation codes → short label + emoji glyph. */
export function weatherCodeInfo(code: number): { label: string; glyph: string } {
  if (code === 0) return { label: t("player.widgets.weather.condition.clear"), glyph: "☀️" };
  if (code === 1 || code === 2)
    return { label: t("player.widgets.weather.condition.partlyCloudy"), glyph: "🌤️" };
  if (code === 3) return { label: t("player.widgets.weather.condition.overcast"), glyph: "☁️" };
  if (code === 45 || code === 48) return { label: t("player.widgets.weather.condition.fog"), glyph: "🌫️" };
  if (code >= 51 && code <= 57) return { label: t("player.widgets.weather.condition.drizzle"), glyph: "🌦️" };
  if (code >= 61 && code <= 67) return { label: t("player.widgets.weather.condition.rain"), glyph: "🌧️" };
  if (code >= 71 && code <= 77) return { label: t("player.widgets.weather.condition.snow"), glyph: "🌨️" };
  if (code >= 80 && code <= 82) return { label: t("player.widgets.weather.condition.showers"), glyph: "🌧️" };
  if (code === 85 || code === 86)
    return { label: t("player.widgets.weather.condition.snowShowers"), glyph: "🌨️" };
  if (code >= 95) return { label: t("player.widgets.weather.condition.thunderstorm"), glyph: "⛈️" };
  return { label: t("player.widgets.weather.condition.unknown"), glyph: "🌡️" };
}

/** True when the cached sample is still fresh enough to skip a fetch. */
export function weatherCacheFresh(fetchedAtMs: number, nowMs: number): boolean {
  return nowMs - fetchedAtMs < WEATHER_CACHE_TTL_MS;
}

// ── ticker ───────────────────────────────────────────────────────────────────

/** config.items: string[] of headlines/messages to scroll. */
export function tickerItems(config: Config): string[] {
  const raw = config?.["items"];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((it): it is string => typeof it === "string")
    .map((it) => it.trim())
    .filter((it) => it.length > 0);
}

/**
 * One full marquee pass duration, scaled to the text length so long feeds
 * do not fly by. ~14 characters/second, clamped to [12s, 120s].
 */
export function tickerDurationSec(items: string[]): number {
  const chars = items.reduce((n, it) => n + it.length + 3, 0);
  return Math.min(120, Math.max(12, Math.round(chars / 14)));
}

// ── qr ───────────────────────────────────────────────────────────────────────

export interface QrConfig {
  url: string;
  caption: string | null;
}

export function qrConfig(config: Config): QrConfig | null {
  const url = str(config, "url");
  if (!url) return null;
  return { url, caption: str(config, "caption") };
}

// ── countdown ────────────────────────────────────────────────────────────────

export interface CountdownConfig {
  targetIso: string;
  label: string | null;
  /** Shown when the target has passed (default "It's time."). */
  doneText: string;
}

export function countdownConfig(config: Config): CountdownConfig | null {
  const targetIso = str(config, "targetIso");
  if (!targetIso || Number.isNaN(new Date(targetIso).getTime())) return null;
  return {
    targetIso,
    label: str(config, "label"),
    doneText: str(config, "doneText") ?? t("player.widgets.countdown.done"),
  };
}

export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  done: boolean;
}

export function countdownParts(targetIso: string, now: Date): CountdownParts {
  const target = new Date(targetIso).getTime();
  const deltaMs = target - now.getTime();
  if (!Number.isFinite(target) || deltaMs <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
  }
  const totalSec = Math.floor(deltaMs / 1000);
  return {
    days: Math.floor(totalSec / 86_400),
    hours: Math.floor((totalSec % 86_400) / 3_600),
    minutes: Math.floor((totalSec % 3_600) / 60),
    seconds: totalSec % 60,
    done: false,
  };
}

export function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

// ── now playing ──────────────────────────────────────────────────────────────

export interface NowPlayingConfig {
  showArtwork: boolean;
  showUpNext: boolean;
  /** Shown when nothing is on air (default from strings). */
  idleText: string;
}

export function nowPlayingConfig(config: Config): NowPlayingConfig {
  return {
    showArtwork: config?.["showArtwork"] !== false,
    showUpNext: config?.["showUpNext"] !== false,
    idleText: str(config, "idleText") ?? t("player.widgets.nowPlaying.idle"),
  };
}

// ── news ─────────────────────────────────────────────────────────────────────

export const NEWS_DEFAULT_ROTATE_SEC = 8;
export const NEWS_DEFAULT_MAX_ITEMS = 10;

export interface NewsConfig {
  maxItems: number;
  rotateSec: number;
}

/** Client-side clamps mirror the server validation (maxItems 1–20, rotate 3–120s). */
export function newsConfig(config: Config): NewsConfig {
  const maxItems = num(config, "maxItems");
  const rotateSec = num(config, "rotateSec");
  return {
    maxItems:
      maxItems !== null && Number.isInteger(maxItems)
        ? Math.min(20, Math.max(1, maxItems))
        : NEWS_DEFAULT_MAX_ITEMS,
    rotateSec:
      rotateSec !== null && Number.isInteger(rotateSec)
        ? Math.min(120, Math.max(3, rotateSec))
        : NEWS_DEFAULT_ROTATE_SEC,
  };
}

/** Which headline is on screen at `nowMs`, cycling every `rotateSec`. */
export function newsRotationIndex(
  itemCount: number,
  rotateSec: number,
  nowMs: number,
): number {
  if (itemCount <= 0) return 0;
  return Math.floor(nowMs / (rotateSec * 1000)) % itemCount;
}

// ── menu board ───────────────────────────────────────────────────────────────

export interface MenuItemConfig {
  name: string;
  price: string | null;
  description: string | null;
}

export interface MenuSectionConfig {
  name: string;
  items: MenuItemConfig[];
}

export interface MenuConfig {
  title: string | null;
  currency: string | null;
  sections: MenuSectionConfig[];
}

/**
 * Parse a menu-board config. Malformed sections/items are dropped rather than
 * failing the whole board (the server validates on write; this reader is the
 * defensive path for rows written before validation tightened). Returns null
 * when nothing renderable remains.
 */
export function menuConfig(config: Config): MenuConfig | null {
  const rawSections = config?.["sections"];
  if (!Array.isArray(rawSections)) return null;
  const sections: MenuSectionConfig[] = [];
  for (const rawSection of rawSections) {
    if (!rawSection || typeof rawSection !== "object" || Array.isArray(rawSection)) continue;
    const sec = rawSection as Record<string, unknown>;
    const name = typeof sec["name"] === "string" ? sec["name"].trim() : "";
    if (!name) continue;
    const rawItems = sec["items"];
    if (!Array.isArray(rawItems)) continue;
    const items: MenuItemConfig[] = [];
    for (const rawItem of rawItems) {
      if (!rawItem || typeof rawItem !== "object" || Array.isArray(rawItem)) continue;
      const it = rawItem as Record<string, unknown>;
      const itemName = typeof it["name"] === "string" ? it["name"].trim() : "";
      if (!itemName) continue;
      items.push({
        name: itemName,
        price: typeof it["price"] === "string" && it["price"].length > 0 ? it["price"] : null,
        description:
          typeof it["description"] === "string" && it["description"].length > 0
            ? it["description"]
            : null,
      });
    }
    if (items.length > 0) sections.push({ name, items });
  }
  if (sections.length === 0) return null;
  return {
    title: str(config, "title"),
    currency: str(config, "currency"),
    sections,
  };
}
